/**
 * React Hook for RTDB-cached NHL player data with real-time updates
 *
 * TO BE IMPLEMENTED: Awaiting NHL API integration
 */

import { useState, useEffect, useCallback } from 'react';
import { rtdbNHLService } from '../rtdbNHLService';
import nhlRTDBCache from '../rtdbCache';
import { CACHE_PATHS, logCache } from '../rtdbConfig';

/**
 * Hook for fetching NHL player statistics with RTDB caching
 * @param {Object} params - Query parameters
 * @param {string} params.playerId - Player ID
 * @param {string} params.season - Season identifier (e.g., '2024-25')
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates
 * @returns {Object} Player data, loading state, error, and refetch function
 */
export const useRTDBPlayers = ({
  playerId,
  season,
  enabled = true,
  realtime = false // Default false for player stats - less need for real-time
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchPlayer = useCallback(async () => {
    if (!enabled || !playerId) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');

      logCache('info', 'useRTDBPlayers fetching - awaiting NHL API integration', {
        playerId,
        season
      });

      // TO BE IMPLEMENTED: Actual NHL API integration
      const response = await rtdbNHLService.getPlayerStats(playerId, season);

      const playerData = response?.response || null;
      setData(playerData);
      setCacheStatus('success');

      logCache('info', `useRTDBPlayers loaded player ${playerId}`);
    } catch (err) {
      setError(err.message || 'Failed to load player data');
      setCacheStatus('error');
      console.error('Failed to load NHL player:', err);
    } finally {
      setLoading(false);
    }
  }, [playerId, season, enabled]);

  // Set up real-time subscription (optional for player stats)
  useEffect(() => {
    if (!enabled || !playerId || !realtime) return;

    let unsubscribe = () => {};

    // Build cache path
    const cachePath = `${CACHE_PATHS.PLAYERS}/player_${playerId}_${season || 'current'}`;

    if (realtime) {
      // Subscribe to cache updates
      unsubscribe = nhlRTDBCache.subscribe(cachePath, (cachedPlayer) => {
        if (cachedPlayer) {
          setData(cachedPlayer);
          setCacheStatus('realtime-update');
          logCache('info', `useRTDBPlayers realtime update for player ${playerId}`);
        }
      });
    }

    return () => {
      unsubscribe();
    };
  }, [playerId, season, enabled, realtime]);

  // Initial fetch
  useEffect(() => {
    fetchPlayer();
  }, [fetchPlayer]);

  return {
    player: data,
    loading,
    error,
    cacheStatus,
    refetch: fetchPlayer
  };
};

export default useRTDBPlayers;
